// client/src/components/itensVenda/ItensVenda.js - VERSÃO 100% COMPLETA E CORRIGIDA
import React, { Fragment, useState, useCallback, useMemo, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useItensVenda, useMutateItensVenda } from '../../hooks/useItensVenda';
import ItemVendaForm from './ItemVendaForm';
import ItemVendaRow from './ItemVendaRow';
import FiltroItensVenda from './FiltroItensVenda';
import { PlusCircle, Filter as FilterIcon, Loader2, ShoppingCart, AlertCircle, XCircle, Eye, Image as ImageIcon, Trash2, RefreshCw, History } from 'lucide-react';
import ActionsDrawer from '../common/ActionsDrawer';
import Pagination from '../common/Pagination';
import { useAuth } from '../../context/auth/AuthProvider';
import PageHeader from '../layout/PageHeader';
import ItemVendaImages from './ItemVendaImages';
import FabButton from '../common/FabButton';
import HistoricoEstoqueModal from './HistoricoEstoqueModal';

const ItensVenda = () => {
    const { user } = useAuth();
    const accountId = user?.account?._id || user?.account;
    const location = useLocation();
    const navigate = useNavigate();

    const [viewState, setViewState] = useState({
        showList: true,
        showForm: false,
        showImageManager: false
    });
    
    const [showFilters, setShowFilters] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [currentItem, setCurrentItem] = useState(null);
    const [activeFilters, setActiveFilters] = useState({});
    const [selectedItem, setSelectedItem] = useState(null);
    const [historicoItem, setHistoricoItem] = useState(null);
    const itemsPerPage = 10;

    // ✅ Prefill vindo do Catálogo de Modelos (ver ImportarCatalogoPage.js —
    // Conta clica "Importar", navega pra cá já com os dados do item). Sem
    // _id, então ItemVendaForm já entra em modo criação pré-preenchido.
    useEffect(() => {
        if (location.state?.prefillCatalogo) {
            setCurrentItem(location.state.prefillCatalogo);
            setViewState({ showList: false, showForm: true, showImageManager: false });
            navigate(location.pathname, { replace: true, state: {} });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.state]);

    const queryParams = useMemo(() => ({
        page: currentPage,
        limit: itemsPerPage,
        ...activeFilters
    }), [currentPage, activeFilters, itemsPerPage]);
    
    const { 
        data: itensVendaData, 
        isLoading, 
        refetch,
        error,
        isError 
    } = useItensVenda(queryParams);
    
    const { 
        deleteItemVenda, 
        isPending: mutationsPending 
    } = useMutateItensVenda();

    const itensVenda = useMemo(() => {
        if (!itensVendaData) return [];
        
        let data = itensVendaData.data || itensVendaData.itens || itensVendaData;
        
        if (data && typeof data === 'object' && !Array.isArray(data)) {
            data = data.data || data.itens || [];
        }
        
        if (!Array.isArray(data)) return [];
        
        return data.map((item, index) => ({
            ...item,
            _id: item._id || item.id || `temp-${index}-${Date.now()}`,
            nome: item.nome || 'Sem nome',
            descricao: item.descricao || '',
            categoria: item.categoria || { nome: 'Sem categoria' },
            unidadesDetalhes: item.unidadesDetalhes || [],
            ativo: item.ativo !== undefined ? item.ativo : true,
            sku: item.sku || '',
            imagens: item.imagens || [],
            imagensDaGaleria: item.imagensDaGaleria || [],
            imagemPrincipal: item.imagemPrincipal || null,
            tags: Array.isArray(item.tags) ? item.tags : []
        }));
    }, [itensVendaData]);

    const totalItens = useMemo(() => {
        if (!itensVendaData) return 0;
        return itensVendaData.totalCount || itensVendaData.total || itensVendaData.count || itensVenda.length;
    }, [itensVendaData, itensVenda.length]);

    // ✅ CORREÇÃO: Sincronizar currentItem quando os dados são atualizados
    useEffect(() => {
        if ((viewState.showForm || viewState.showImageManager) && currentItem?._id) {
            const updatedItem = itensVenda.find(i => i._id === currentItem._id);
            
            if (updatedItem && shouldSyncItem(updatedItem, currentItem)) {
                console.log('🔄 [ITENS_VENDA] Sincronizando currentItem com dados atualizados');
                setCurrentItem(prev => ({ ...prev, ...updatedItem }));
            }
        }
    }, [itensVenda, currentItem?._id, viewState.showForm, viewState.showImageManager]);

    const shouldSyncItem = (newItem, currentItem) => {
        if (!newItem || !currentItem) return false;
        return (
            newItem.nome !== currentItem.nome ||
            newItem.descricao !== currentItem.descricao ||
            newItem.sku !== currentItem.sku ||
            JSON.stringify(newItem.tags) !== JSON.stringify(currentItem.tags) ||
            JSON.stringify(newItem.imagens) !== JSON.stringify(currentItem.imagens) ||
            JSON.stringify(newItem.unidadesDetalhes) !== JSON.stringify(currentItem.unidadesDetalhes)
        );
    };

    const handleAdicionarClick = useCallback(() => { 
        setCurrentItem(null); 
        setViewState({ showList: false, showForm: true, showImageManager: false });
        setShowFilters(false);
    }, []);

    const handleEditClick = useCallback((item) => {
        setCurrentItem(item);
        setViewState({ showList: false, showForm: true, showImageManager: false });
        setShowFilters(false);
        setSelectedItem(null);
    }, []);

    const handleImageClick = useCallback((item) => {
        setCurrentItem(item);
        setViewState({ showList: false, showForm: false, showImageManager: true });
        setShowFilters(false);
        setSelectedItem(null);
    }, []);

    const handleHistoricoClick = useCallback((item) => {
        setHistoricoItem(item);
        setSelectedItem(null);
    }, []);

    const handleFiltrosClick = useCallback(() => { 
        setShowFilters(prev => !prev); 
    }, []);

    const handleCloseForms = useCallback(() => { 
        setViewState({ showList: true, showForm: false, showImageManager: false }); 
        setCurrentItem(null); 
    }, []);

    const handleImageUpdate = useCallback((updatedItem) => {
        setCurrentItem(updatedItem);
    }, []);

    const paginate = useCallback((page) => { 
        if (page !== currentPage) {
            setCurrentPage(page); 
        }
    }, [currentPage]);
    
    const handleDelete = useCallback((id) => {
        if (window.confirm('Tem certeza que deseja excluir este produto?')) {
            deleteItemVenda.mutate(id, {
                onSuccess: () => {
                    setSelectedItem(null);
                    if (itensVenda.length === 1 && currentPage > 1) {
                        setCurrentPage(prev => prev - 1);
                    } else {
                        refetch();
                    }
                }
            });
        }
    }, [deleteItemVenda, itensVenda.length, currentPage, refetch]);

    const handleFilterSubmit = useCallback((filters) => {
        setActiveFilters(filters);
        setCurrentPage(1);
    }, []);

    const handleClearFilters = useCallback(() => {
        setActiveFilters({});
        setCurrentPage(1);
    }, []);

    const isDeletingItem = deleteItemVenda.isPending;
    const isViewingForm = viewState.showForm || viewState.showImageManager;
    
    const getSafeItemId = (item) => {
        if (!item || !item._id) return 'ID não disponível';
        return item._id.substring(0, 8) + '...';
    };

    return (
        <Fragment>
            <div className="page-management-container">
                <PageHeader
                    icon={<ShoppingCart />}
                    title="Gerenciamento de Produtos"
                    actions={<>
                        {!isViewingForm && (
                            <>
                                <button
                                    onClick={handleFiltrosClick}
                                    className={`action-button ${showFilters ? 'action-button-primary' : 'action-button-secondary'}`}
                                    disabled={isLoading || mutationsPending}
                                >
                                    <FilterIcon size={18} className="icon"/>
                                    {showFilters ? 'Ocultar Filtros' : 'Filtros'}
                                </button>
                            </>
                        )}
                        {isViewingForm && (
                            <button onClick={handleCloseForms} className="action-button action-button-secondary" disabled={isLoading || mutationsPending}>
                                <XCircle size={18} className="icon"/> Fechar
                            </button>
                        )}
                    </>}
                />

                {isError && !isLoading && (
                    <div className="content-panel">
                        <div className="alert alert-danger">
                            <AlertCircle size={20} className="icon" />
                            <div>
                                <strong>Erro ao carregar produtos:</strong>
                                <p>{error?.message || 'Erro desconhecido'}</p>
                                {/* .btn/.btn-sm/.btn-outline nao seguiam o padrao
                                    action-button do app — .btn-outline nem tinha CSS
                                    em lugar nenhum (renderizava sem estilo) */}
                                <div className="mt-2 flex gap-2">
                                    <button
                                        onClick={refetch}
                                        className="action-button action-button-primary"
                                        disabled={isLoading}
                                    >
                                        <RefreshCw size={18} className="icon" />
                                        Tentar Novamente
                                    </button>
                                    <button
                                        onClick={handleClearFilters}
                                        className="action-button action-button-secondary"
                                    >
                                        Limpar Filtros
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {viewState.showForm && currentItem && (
                    <div className="content-panel">
                        <ItemVendaForm
                            key={currentItem._id}
                            current={currentItem}
                            onCloseForm={handleCloseForms}
                            onSaveSuccess={handleCloseForms}
                        />
                    </div>
                )}

                {viewState.showImageManager && currentItem && (
                    <div className="content-panel">
                        <div className="form-header">
                            <h3 className="form-title">
                                Editar Imagens: {currentItem.nome}
                                <span className="ml-2 text-sm font-normal bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                                    ID: {getSafeItemId(currentItem)}
                                </span>
                            </h3>
                            <button type="button" onClick={handleCloseForms} title="Voltar" className="form-close-button">
                                <XCircle size={24} />
                            </button>
                        </div>
                        <ItemVendaImages
                            key={`images-${currentItem._id}`}
                            item={currentItem}
                            onUpdate={handleImageUpdate}
                        />
                    </div>
                )}

                {viewState.showForm && !currentItem && (
                    <div className="content-panel">
                        <ItemVendaForm
                            current={null}
                            onCloseForm={handleCloseForms}
                            onSaveSuccess={handleCloseForms}
                        />
                    </div>
                )}

                {showFilters && viewState.showList && (
                    <div className="content-panel">
                        <div className="panel-header">
                            <h3 className="panel-title">Filtrar Produtos</h3>
                            {Object.keys(activeFilters).length > 0 && (
                                <button 
                                    onClick={handleClearFilters}
                                    className="text-sm text-red-600 hover:text-red-800 font-medium"
                                >
                                    Limpar Filtros
                                </button>
                            )}
                        </div>
                        <FiltroItensVenda 
                            onFilterSubmit={handleFilterSubmit} 
                            loading={isLoading}
                            accountId={accountId}
                        />
                    </div>
                )}
            </div>

            {viewState.showList && (
                <div className="table-container">
                    <div className="table-wrapper">
                        {isLoading ? (
                            <div className="table-loading-state">
                                <Loader2 size={24} className="animate-spin" />
                                <span>Carregando produtos...</span>
                            </div>
                        ) : itensVenda.length > 0 ? (
                            <table className="styled-table">
                                <thead>
                                    <tr>
                                        <th>Nome</th>
                                        <th>Categoria</th>
                                        <th>SKU</th>
                                        <th className="text-center">Status</th>
                                        <th className="text-right">Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {itensVenda.map((item, index) =>
                                        <ItemVendaRow
                                            key={item._id || `item-${index}`}
                                            item={item}
                                            onOpenActions={setSelectedItem}
                                            loading={isDeletingItem && deleteItemVenda.variables === item._id}
                                        />
                                    )}
                                </tbody>
                            </table>
                        ) : (
                            <div className="table-empty-state">
                                <ShoppingCart size={48} className="empty-icon" />
                                <h3>Nenhum produto encontrado</h3>
                                <p>
                                    {Object.keys(activeFilters).length > 0 
                                        ? 'Nenhum produto corresponde aos filtros aplicados.' 
                                        : 'Clique em "Adicionar Produto" para adicionar o primeiro.'
                                    }
                                </p>
                                {Object.keys(activeFilters).length > 0 && (
                                    <button
                                        onClick={handleClearFilters}
                                        className="action-button action-button-secondary"
                                    >
                                        Limpar Filtros
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                    <Pagination
                        itemsPerPage={itemsPerPage}
                        totalItems={totalItens}
                        onPageChange={paginate}
                        currentPage={currentPage}
                    />
                </div>
            )}

            <FabButton
                onClick={handleAdicionarClick}
                title="Adicionar Produto"
                visible={!isViewingForm}
                disabled={isLoading || mutationsPending}
                icon={<ShoppingCart size={26} className="icon"/>}
            />

            <ActionsDrawer
                open={!!selectedItem}
                onClose={() => setSelectedItem(null)}
                title={selectedItem?.nome || 'Item de Venda'}
                actions={selectedItem ? [
                    {
                        key: 'imagens',
                        label: 'Gerenciar Imagens',
                        icon: <ImageIcon size={20} className="icon" />,
                        onClick: () => handleImageClick(selectedItem),
                    },
                    {
                        key: 'editar',
                        label: 'Visualizar/Editar',
                        icon: <Eye size={20} className="icon" />,
                        onClick: () => handleEditClick(selectedItem),
                    },
                    // ✅ Só faz sentido pra produtos que de fato controlam
                    // estoque — os demais nunca têm entrada no ledger.
                    ...(selectedItem.controlaEstoque ? [{
                        key: 'historico-estoque',
                        label: 'Histórico de Estoque',
                        icon: <History size={20} className="icon" />,
                        onClick: () => handleHistoricoClick(selectedItem),
                    }] : []),
                    {
                        key: 'excluir',
                        label: 'Excluir',
                        icon: <Trash2 size={20} className="icon" />,
                        onClick: () => handleDelete(selectedItem._id),
                        destructive: true,
                        loading: isDeletingItem && deleteItemVenda.variables === selectedItem._id,
                    },
                ] : []}
            />

            {historicoItem && (
                <HistoricoEstoqueModal
                    item={historicoItem}
                    onClose={() => setHistoricoItem(null)}
                />
            )}
        </Fragment>
    );
};

export default ItensVenda;